import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, TextField, Button, Typography, InputAdornment, IconButton, CircularProgress, Snackbar, FormControlLabel, Checkbox, Stack, Divider } from '@mui/material';
import { useDispatch } from 'react-redux';
import Img from '../../../images/Phyfarm-App-footer.png'
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { loginSuccess } from '../../../reducers/reducers';
import { useNavigate } from 'react-router-dom'
import { Alert } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {baseURL} from '../../../config';
import AuthLayout from '../../../shared/layouts/AuthLayout';
import { authenticateUser, validateUserSession } from '../services/authService';
import GuestFarmDialogs from './GuestFarmDialogs';


const LoginPage = () => {

    const dispatch = useDispatch();


    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const isSmScreen = useMediaQuery('(max-width:850px)');
    const [rememberMe, setRememberMe] = useState(false);

    useEffect(() => {
        const storedEmail = localStorage.getItem('rememberedEmail');
        const storedRememberMe = localStorage.getItem('rememberMe');

        if (storedRememberMe === 'true' && storedEmail) {
            setEmail(storedEmail);
            setRememberMe(true);
        }
    }, []);


    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleForgotPassword = () => {
        navigate('/forgotPassword', { replace: true });
    };

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
    };


    const getUserDataFromLocalStorage = useCallback(async () => {

        const userDataFromLocalStorage = localStorage.getItem('userData');
        const getRememberMe = localStorage.getItem('rememberMe');

        if (userDataFromLocalStorage) {
            try {
                const parsed = JSON.parse(userDataFromLocalStorage);

                const response = await validateUserSession(baseURL, parsed.access_token);
                const result = await response.json();

                if (result.data) {
                    if (getRememberMe) {
                        // Everyone resumes on /home (the nav hub).
                        navigate('/home', { replace: true });
                    } else {
                        // Not a remembered session — unchanged behaviour.
                        localStorage.removeItem('userData');
                    }
                } else {
                    localStorage.removeItem('userData');
                }
            } catch (error) {
                console.error('[LoginPage] Authentication check error:', error);
            }
        }
    }, [navigate]);

    useEffect(() => {
        getUserDataFromLocalStorage();
    }, [getUserDataFromLocalStorage]);

    const handleLogin = async () => {

        if (email === '' || password === '') {
            setError('Empty fields');
            setSnackbarOpen(true);
            return;
        }
        try {

            setLoading(true);

            const response = await authenticateUser(baseURL, email, password);
            const data = await response.json();

            // Success is decided by the HTTP status AND a usable payload — not by the
            // absence of an `error` key alone. Every other endpoint in this app answers
            // with the {code, message, data} envelope, which carries no `error` field:
            // treating that as success wrote userData="undefined" (JSON.stringify of
            // undefined) and dropped the operator on a blank page with a poisoned
            // session. Same for a 5xx/gateway body that happens to parse as JSON.
            const failed = !response.ok || data.error || !data.data;

            if (!failed) {

                dispatch(loginSuccess(data.data));

                if (rememberMe) {
                    localStorage.setItem('rememberedEmail', email);
                    localStorage.setItem('rememberMe', true);
                } else {
                    localStorage.removeItem('rememberedEmail');
                    localStorage.removeItem('rememberMe');
                }

                // Defensive: drop any previous account's cached session so this
                // login never inherits it (checkAuth repopulates for THIS account).
                ['permissions', 'agroUser'].forEach((k) => localStorage.removeItem(k));
                localStorage.setItem('userData', JSON.stringify(data.data));

                // EVERYONE lands on /home — the nav hub, whose quick-action cards are
                // scoped to the caller's grants. The sidebar handles per-tab
                // navigation from there.
                navigate('/home', { replace: true });
            } else {
                setLoading(false);
                // Tolerate every failure shape: {error:{code}}, the standard
                // {code, message} envelope, or a bare non-2xx with no useful body.
                setError(
                    data?.error?.code ||
                    data?.message ||
                    `Could not sign in (HTTP ${response.status})`
                );
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('[LoginPage] Login error:', error);
            setError('An error occurred while trying to log in.');
            setLoading(false);
            setSnackbarOpen(true);
        }
    };

    const handleRememberMeChange = (event) => {
        setRememberMe(event.target.checked);
    };

    return (
        <AuthLayout fullBleed>
            <Box
                sx={{
                    minHeight: '100vh',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 6,
                    backgroundImage: `url(${Img})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(135deg, rgba(8,28,18,0.92) 0%, rgba(10,46,31,0.82) 45%, rgba(245,244,238,0.70) 100%)',
                    }}
                />

                <Paper
                    elevation={0}
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        maxWidth: 980,
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.14)',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(14px)',
                        background: 'rgba(255,255,255,0.10)',
                    }}
                >
                    <Box sx={{ display: 'grid', gridTemplateColumns: isSmScreen ? '1fr' : '1.15fr 0.85fr' }}>
                        {/* Brand / Info */}
                        <Box
                            sx={{
                                p: isSmScreen ? 3 : 4,
                                color: 'white',
                                background:
                                    'linear-gradient(160deg, rgba(8,28,18,0.70) 0%, rgba(10,46,31,0.40) 55%, rgba(10,46,31,0.15) 100%)',
                                borderRight: isSmScreen ? 'none' : '1px solid rgba(255,255,255,0.10)',
                            }}
                        >
                            <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: '0.18em', opacity: 0.7 }}>
                                PHYFARM
                            </Typography>
                            <Typography sx={{ mt: 0.75, fontFamily: '"Playfair Display", serif', fontWeight: 900, fontSize: 34, lineHeight: 1.05 }}>
                                Farm Intelligence Platform
                            </Typography>
                            <Typography sx={{ mt: 1.25, color: 'rgba(167,243,208,0.85)', fontFamily: '"DM Sans", sans-serif', maxWidth: 440 }}>
                                Log in to manage farms, map plots & water sources, upload farm media, and generate drip irrigation designs.
                            </Typography>

                            {!isSmScreen ? (
                                <Box sx={{ mt: 3.5 }}>
                                    <Stack spacing={1.25}>
                                        {[
                                            { title: 'Fast Mapping', desc: 'Pin farm, mark plots & sources on map.' },
                                            { title: 'Farm Media', desc: 'Upload images and keep records organized.' },
                                            { title: 'Design Insights', desc: 'Generate design and BOM in one click.' },
                                        ].map((f) => (
                                            <Box key={f.title} sx={{ display: 'flex', gap: 1.25 }}>
                                                <Box sx={{ mt: 0.55, width: 10, height: 10, borderRadius: 999, background: 'linear-gradient(135deg, #2ECC7A, #6EE7B7)' }} />
                                                <Box>
                                                    <Typography sx={{ fontWeight: 800, fontFamily: '"DM Sans", sans-serif' }}>{f.title}</Typography>
                                                    <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.70)' }}>{f.desc}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            ) : null}
                        </Box>

                        {/* Form */}
                        <Box
                            sx={{
                                p: isSmScreen ? 3 : 4,
                                background: 'rgba(250,250,245,0.92)',
                            }}
                        >
                            <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: '0.16em', color: '#1A7A50' }}>
                                WELCOME BACK
                            </Typography>
                            <Typography sx={{ mt: 0.6, fontFamily: '"Playfair Display", serif', fontWeight: 800, fontSize: 26, color: '#0A2E1F' }}>
                                Sign in
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: '#6B8A7A', fontSize: 13 }}>
                                Use your Phyfarm admin credentials to continue.
                            </Typography>

                            <Divider sx={{ my: 2.5, borderColor: 'rgba(10,46,31,0.10)' }} />

                            <Stack spacing={1.75}>
                                <TextField
                                    label="Email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    error={email === '' && error !== ''}
                                    placeholder="name@company.com"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: 'rgba(10,46,31,0.45)' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    label="Password"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    type={showPassword ? 'text' : 'password'}
                                    error={password === '' && error !== ''}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VpnKeyIcon sx={{ color: 'rgba(10,46,31,0.45)' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={togglePasswordVisibility} edge="end" size="small">
                                                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.25 }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={rememberMe} onChange={handleRememberMeChange} color="success" />}
                                        label={<Typography sx={{ fontSize: 13, color: '#0A2E1F' }}>Remember me</Typography>}
                                    />
                                    <Button
                                        onClick={handleForgotPassword}
                                        variant="text"
                                        sx={{ textTransform: 'none', fontSize: 13, color: '#1A7A50', fontWeight: 700 }}
                                    >
                                        Forgot password?
                                    </Button>
                                </Stack>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleLogin}
                                    disabled={loading}
                                    sx={{
                                        mt: 0.5,
                                        py: 1.1,
                                        fontWeight: 800,
                                        background: 'linear-gradient(135deg, #0F4D33 0%, #1A7A50 55%, #2ECC7A 100%)',
                                        boxShadow: '0 10px 30px rgba(26,122,80,0.28)',
                                        '&:hover': { filter: 'brightness(1.05)' },
                                    }}
                                >
                                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Log In'}
                                </Button>

                                <GuestFarmDialogs />

                                <Typography sx={{ mt: 1.5, fontSize: 11, color: '#6B8A7A', textAlign: 'center', fontFamily: '"DM Mono", monospace' }}>
                                    © {new Date().getFullYear()} Phyfarm
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Paper>
            </Box>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Alert onClose={handleSnackbarClose} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </AuthLayout>

    );
};

export default LoginPage;
